<script lang="ts">
  import { onDestroy } from 'svelte';
  import { useNavigate } from 'svelte-navigator';

  import {
    DataTable,
    InlineNotification,
    Link,
    OverflowMenu,
    OverflowMenuItem,
    Toolbar,
    ToolbarContent,
    TooltipDefinition,
  } from 'carbon-components-svelte';
  import type { DataTableHeader, DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';
  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import debounce from 'lodash/debounce';
  import isEqual from 'lodash/isEqual';
  import startCase from 'lodash/startCase';

  import Badge from '../../components/Badge.svelte';
  import BuildNotesModal from '../../components/build-parameters/BuildNotesModal.svelte';
  import BuildOverviewModal from '../../components/build-parameters/BuildOverviewModal.svelte';
  import RunBuildModal from '../../components/build-parameters/RunBuildModal.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import RelativeDate from '../../components/RelativeDate.svelte';
  import ToolbarSearchWithRouting from '../../components/ToolbarSearchWithRouting.svelte';
  import StatusIcon from './BuildStatusIcon.svelte';
  import JsonPassiveModal from './JsonPassiveModal.svelte';

  import { getTriggeredBuilds, type BuildNote, type TriggeredBuild } from '../../services';
  import { appConfig } from '../../stores';
  import { noopSort, parseSortEvent } from '../../utils';

  const navigate = useNavigate();

  const FETCH_HISTORY_DEBOUNCE_MS = 500;
  const FETCH_HISTORY_INTERVAL_MS = 5000;

  let loading = true;
  let searchTextInitialized = false;
  let paginationInitialized = false;

  let builds: TriggeredBuild[] = [];
  let totalItems: number = 0;
  let resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind' | 'hideCloseButton'>> | null = null;

  let fetchInterval: ReturnType<typeof setInterval> | null = null;
  let activeAbortController: AbortController | null = null;

  const criteria = {
    searchText: '',
    currentPage: 1,
    pageSize: 25,
    orderBy: null as string | null,
    orderDirection: null as 'ASC' | 'DESC' | null,
  };
  let prevCriteria: Partial<typeof criteria> = {};

  type ModalKey = 'logs' | 'config' | 'notes' | 'overview' | 'runBuild';

  const modals: Record<ModalKey, { open: false } | { open: true; row: TriggeredBuild }> = {
    logs: { open: false },
    config: { open: false },
    notes: { open: false },
    overview: { open: false },
    runBuild: { open: false },
  };

  const tableHeaders: DataTableHeader[] = [
    { key: 'group.name', value: 'Group', sort: noopSort },
    { key: 'buildConfig.name', value: 'Target', sort: noopSort },
    { key: 'status', value: 'Status', display: (status: string) => startCase(status), sort: noopSort },
    { key: 'creator.name', value: 'Triggered By', sort: noopSort },
    { key: 'createdAt', value: 'Triggered', sort: noopSort },
    { key: 'actions', empty: true, sort: false },
  ];

  function onSearchTextInit() {
    searchTextInitialized = true;
  }

  function onPaginationInit() {
    paginationInitialized = true;
  }

  function goToBuild({ organization, group, target }: { organization: string; group: string; target: string }) {
    return function openWindow() {
      window.open(
        `${$appConfig.data.jenkinsUrl}/job/customer_builds/job/${organization}/job/${group}/job/${organization}-${group}-${target}/`,
        '_blank'
      );
    };
  }

  function goToEntity(id: string, targetId: string, type = 'group') {
    return function go(event: MouseEvent) {
      event.preventDefault();
      navigate(`/${type}/${id}?target=${targetId}#parameters`);
    };
  }

  const onSortChange = (event: CustomEvent) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      criteria.orderBy = orderBy;
      criteria.orderDirection = orderDirection;
    });

  async function fetchBuildsHistory({ searchText, currentPage, pageSize, orderBy, orderDirection }: typeof criteria, showLoader = false) {
    // Cancel any in-flight request
    if (activeAbortController) {
      activeAbortController.abort();
    }

    const abortController = new AbortController();
    activeAbortController = abortController;

    loading = showLoader;

    try {
      const { items, count } = await getTriggeredBuilds(
        { search: searchText || undefined },
        { orderBy, orderDirection, currentPage, pageSize },
        { signal: abortController.signal }
      );

      // Ignore if this request was superseded
      if (abortController.signal.aborted) return;

      builds = items;
      totalItems = count;
      resultNotification = null;

      if (!items.length) {
        resultNotification = {
          hideCloseButton: true,
          kind: 'warning',
          title: 'No builds:',
          subtitle: 'There are not any builds triggered yet',
        };
      }
    } catch (error) {
      // Ignore aborted requests
      if ((error as Error)?.name === 'CanceledError' || abortController.signal.aborted) return;

      console.error('[TriggeredBuildsList] Failed to fetch triggered builds', error);

      resultNotification = {
        hideCloseButton: true,
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to get/refresh builds history. Please try refreshing the page',
      };

      clearAutoRefresh();
    } finally {
      if (!abortController.signal.aborted) {
        loading = false;
      }
      if (activeAbortController === abortController) {
        activeAbortController = null;
      }
    }
  }

  function startAutoRefresh() {
    clearAutoRefresh();
    fetchInterval = setInterval(() => fetchBuildsHistory(criteria), FETCH_HISTORY_INTERVAL_MS);
  }

  function clearAutoRefresh() {
    if (fetchInterval) {
      clearInterval(fetchInterval);
      fetchInterval = null;
    }
  }

  /** Fetch triggered by user action (search/pagination change). Resets the auto-refresh timer. */
  function fetchAndResetInterval(opts: typeof criteria) {
    fetchBuildsHistory(opts, true);
    startAutoRefresh();
  }

  const fetchAndResetIntervalDebounced = debounce(fetchAndResetInterval, FETCH_HISTORY_DEBOUNCE_MS);

  function updateBuildData(
    modification:
      | { action: 'note-deletion'; data: { entityId: string; noteId: string } }
      | { action: 'note-creation' | 'note-update'; data: { entityId: string; note: BuildNote } }
  ) {
    const { action, data } = modification;
    if (!data) return;

    const buildIndex = builds.findIndex(({ id }) => id === data.entityId);
    const build = builds[buildIndex];
    if (!build) return;

    if (action === 'note-deletion') {
      build.buildNotes = build.buildNotes.filter(({ id }) => id !== data.noteId);
    } else if (action === 'note-creation') {
      build.buildNotes = [...build.buildNotes, data.note];
    } else if (action === 'note-update') {
      build.buildNotes = build.buildNotes.map((note) => (note.id !== data.note.id ? note : data.note));
    } else {
      console.warn(`[TriggeredBuildsList] Unknown action "${action}". Data:`, data);
      return;
    }

    builds[buildIndex] = build;
    if (modals.notes.open) modals.notes.row = build;
  }

  function closeModal(name: ModalKey) {
    modals[name] = { open: false };
  }

  function handleClickForModal(name: ModalKey, row: DataTableRow) {
    return function openModal() {
      modals[name] = { open: true, row: row as TriggeredBuild };
    };
  }

  onDestroy(() => {
    clearAutoRefresh();
    fetchAndResetIntervalDebounced.cancel();
    activeAbortController?.abort();
  });

  $: if (searchTextInitialized && paginationInitialized && !isEqual(criteria, prevCriteria)) {
    const initialized = Object.keys(prevCriteria).length > 0;
    const searchChanged = prevCriteria.searchText !== criteria.searchText;

    if (!initialized || !searchChanged) {
      fetchAndResetInterval(criteria);
    } else {
      if (criteria.currentPage !== 1) {
        criteria.currentPage = 1;
      }
      fetchAndResetIntervalDebounced(criteria);
    }

    prevCriteria = { ...criteria };
  }
</script>

<LoadingSpinner {loading}>
  {#if resultNotification}
    <InlineNotification
      hideCloseButton={resultNotification.hideCloseButton}
      kind={resultNotification.kind}
      lowContrast
      title={resultNotification.title}
      subtitle={resultNotification.subtitle}
    />
  {/if}
  <DataTable headers={tableHeaders} rows={builds ?? []} sortable on:click:header={onSortChange}>
    <div class:action-cell={cell.key === 'actions'} slot="cell" let:cell let:row>
      {#if cell.key === 'actions'}
        <Badge active={!!row.buildNotes.length} value={row.buildNotes.length}>
          <OverflowMenu aria-label="Open and close list of options" flipped portalMenu direction="bottom">
            <OverflowMenuItem text="View build overview" on:click={handleClickForModal('overview', row)} />
            <OverflowMenuItem text="View logs" on:click={handleClickForModal('logs', row)} />
            <OverflowMenuItem text="View triggered JSON" on:click={handleClickForModal('config', row)} />
            <OverflowMenuItem on:click={handleClickForModal('notes', row)}>
              <Badge active={!!row.buildNotes.length} value={row.buildNotes.length} position="middle-right">Notes</Badge>
            </OverflowMenuItem>
            <OverflowMenuItem text="Go to Jenkins" on:click={goToBuild(row.extra.sanitizedNames)} />
            <OverflowMenuItem text="Re-run build" on:click={handleClickForModal('runBuild', row)} disabled={row.buildConfig?.deletedAt} />
          </OverflowMenu>
        </Badge>
      {:else if cell.key === 'status'}
        <div class="flex-cell">
          <StatusIcon status={cell.value} details={row.extra} />
          {startCase(cell.value)}
        </div>
      {:else if cell.key === 'creator.name'}
        <div class="flex-cell flex-column">
          <span>{row.creator?.name ?? '—'}</span>
          {#if row.creator?.name !== row.creator?.email}
            <em>{row.creator?.email}</em>
          {/if}
        </div>
      {:else if cell.key === 'group.name'}
        <div class="flex-cell flex-column">
          <strong>{row.group?.name}</strong>
          <em>{row.organization?.name}</em>
        </div>
      {:else if cell.key === 'buildConfig.name' && row.buildConfig}
        {#if row.buildConfig.deletedAt}
          <TooltipDefinition tooltipText="Deleted target" portalTooltip>
            {row.buildConfig.name}
          </TooltipDefinition>
        {:else}
          <Link href={`/group/${row.groupId}?target=${row.configId}#parameters`} on:click={goToEntity(row.groupId, row.configId)}>
            {row.buildConfig.name}
          </Link>
        {/if}
      {:else if cell.key === 'createdAt'}
        <RelativeDate date={cell.value} />
      {:else}
        {cell.display ? cell.display(cell.value, row) : cell.value ?? ''}
      {/if}
    </div>
    <Toolbar>
      <ToolbarContent>
        <ToolbarSearchWithRouting
          placeholder="Search builds..."
          persistent
          bind:searchText={criteria.searchText}
          on:toolbarSearchValueInit={onSearchTextInit}
        />
      </ToolbarContent>
    </Toolbar>
  </DataTable>
  <PaginationWithRouting
    bind:page={criteria.currentPage}
    bind:pageSize={criteria.pageSize}
    pageSizes={[10, 25, 50]}
    {totalItems}
    on:init={onPaginationInit}
  />
  {#if modals.logs.open}
    <JsonPassiveModal open name="Build Logs" data={modals.logs.row.extra} on:close={() => closeModal('logs')} />
  {/if}
  {#if modals.config.open}
    <JsonPassiveModal
      open
      name="Triggered Build Configuration"
      data={modals.config.row.triggeredConfig}
      on:close={() => closeModal('config')}
    />
  {/if}
  {#if modals.notes.open}
    <BuildNotesModal
      open
      notes={modals.notes.row.buildNotes}
      entityId={modals.notes.row.id}
      on:refresh={({ detail: data }) => updateBuildData(data)}
      on:close={() => closeModal('notes')}
    />
  {/if}
  {#if modals.overview.open}
    <BuildOverviewModal
      open
      jsonConfig={modals.overview.row.triggeredConfig}
      targetName={modals.overview.row.buildConfig?.name}
      entityName={modals.overview.row.group?.name}
      on:close={() => closeModal('overview')}
    />
  {/if}
  {#if modals.runBuild.open}
    <RunBuildModal
      open
      runningPreviousBuild
      configurations={[
        {
          configId: modals.runBuild.row.configId,
          entityId: modals.runBuild.row.groupId,
          entityType: 'group',
          entityName: modals.runBuild.row.group?.name,
          targetName: modals.runBuild.row.buildConfig?.name,
          triggeredConfig: modals.runBuild.row.triggeredConfig,
        },
      ]}
      on:close={() => closeModal('runBuild')}
    />
  {/if}
</LoadingSpinner>

<style>
  .flex-cell {
    display: flex;
  }

  .flex-column {
    flex-direction: column;
  }

  .flex-cell :global(svg) {
    margin-left: 0.5rem;
  }

  .flex-cell :global(em) {
    font-size: 12px;
  }

  .action-cell {
    width: 40px;
  }
</style>
